import React, { useState, useEffect } from 'react';
import { getData, postData, putData, deleteData } from '../helpers/ApiSettings';

const PreOrders = () => {
  const [preOrders, setPreOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedPreOrder, setSelectedPreOrder] = useState(null);
  const [formData, setFormData] = useState({
    product: '',
    start_date: '',
    end_date: '',
    price: '',
    pre_order: ''
  });

  const fetchPreOrders = async () => {
    try {
      setLoading(true);
      const response = await getData('/pre-orders?populate[pre_order_items][populate][0]=product');
    //   console.log(response.data.data);
      setPreOrders(response.data.data);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des pré-commandes');
      console.error('Error fetching pre-orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await getData('/products?sort[0]=titre:asc');
      setProducts(response.data.data);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  useEffect(() => {
    fetchPreOrders();
    fetchProducts();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        data: {
          ...formData,
          price: parseFloat(formData.price),
          pre_order: selectedPreOrder.id
        }
      };

      if (editingItem) {
        await putData(`/pre-order-items/${editingItem.documentId}`, payload);
        alert('Article modifié avec succès');
      } else {
        await postData('/pre-order-items', payload);
        alert('Article ajouté avec succès');
      }
      
      const modal = document.getElementById('preOrderItemModal');
      const bootstrapModal = window.bootstrap.Modal.getInstance(modal);
      bootstrapModal.hide();
      
      setFormData({
        product: '',
        start_date: '',
        end_date: '',
        price: '',
        pre_order: ''
      });
      setEditingItem(null);
      await fetchPreOrders();
    } catch (err) {
      alert('Erreur lors de l\'enregistrement de l\'article');
      console.error('Error saving pre-order item:', err);
    }
  };

  const handleCreatePreOrder = async () => {
    try {
      const response = await postData('/pre-orders', {
        data: { deposit: 0 }
      });
      await fetchPreOrders();
      return response.data.data;
    } catch (err) {
      alert('Erreur lors de la création de la pré-commande');
      console.error('Error creating pre-order:', err);
    }
  };

  const handleAddItem = async (preOrder) => {
    setSelectedPreOrder(preOrder);
    setEditingItem(null);
    setFormData({
      product: '',
      start_date: '',
      end_date: '',
      price: '',
      pre_order: preOrder.id
    });
    const modal = new window.bootstrap.Modal(document.getElementById('preOrderItemModal'));
    modal.show();
  };

  const handleEditItem = (item, preOrder) => {
    setSelectedPreOrder(preOrder);
    setEditingItem(item);
    setFormData({
      product: item.product?.id || '',
      start_date: item.start_date?.split('T')[0] || '',
      end_date: item.end_date?.split('T')[0] || '',
      price: item.price || '',
      pre_order: preOrder.id
    });
    const modal = new window.bootstrap.Modal(document.getElementById('preOrderItemModal'));
    modal.show();
  };

  const handleDeleteItem = async (itemId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) {
      try {
        await deleteData(`/pre-order-items/${itemId}`);
        await fetchPreOrders();
        alert('Article supprimé avec succès');
      } catch (err) {
        alert('Erreur lors de la suppression de l\'article');
        console.error('Error deleting pre-order item:', err);
      }
    }
  };

  const handleDeletePreOrder = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette pré-commande et tous ses articles ?')) {
      try {
        await deleteData(`/pre-orders/${id}`);
        await fetchPreOrders();
        alert('Pré-commande supprimée avec succès');
      } catch (err) {
        alert('Erreur lors de la suppression de la pré-commande');
        console.error('Error deleting pre-order:', err);
      }
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="h4 mb-0">Pré-commandes</h2>
        <div>
          <button 
            onClick={fetchPreOrders} 
            className="btn btn-outline-primary me-2"
          >
            Actualiser
          </button>
          <button 
            onClick={handleCreatePreOrder}
            className="btn btn-primary"
          >
            Nouvelle Pré-commande
          </button>
        </div>
      </div>

      {preOrders.length === 0 ? (
        <div className="alert alert-info">
          Aucune pré-commande pour le moment
        </div>
      ) : (
        preOrders.map((preOrder) => (
          <div key={preOrder.id} className="card mb-4">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Pré-commande #{preOrder.id}</h5>
              <div>
                <button 
                  onClick={() => handleAddItem(preOrder)}
                  className="btn btn-sm btn-success me-2"
                >
                  Ajouter un article
                </button>
                <button
                  onClick={() => handleDeletePreOrder(preOrder.documentId)}
                  className="btn btn-sm btn-danger"
                >
                  Supprimer la pré-commande
                </button>
              </div>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Produit</th>
                      <th>Date de début</th>
                      <th>Date de fin</th>
                      <th>Prix</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preOrder.pre_order_items?.map((item) => (
                      <tr key={item.id}>
                        <td>{item.product?.titre || '-'}</td>
                        <td>{formatDate(item.start_date)}</td>
                        <td>{formatDate(item.end_date)}</td>
                        <td>{formatPrice(item.price)}</td>
                        <td>
                          <div className="btn-group">
                            <button
                              className="btn btn-sm btn-primary me-2"
                              onClick={() => handleEditItem(item, preOrder)}
                            >
                              Modifier
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDeleteItem(item.documentId)}
                            >
                              Supprimer
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {(!preOrder.pre_order_items || 
                      preOrder.pre_order_items.length === 0) && (
                      <tr>
                        <td colSpan="5" className="text-center text-muted">
                          Aucun article dans cette pré-commande
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="card-footer">
                Lien de pré-commande : https://ovlm97.fr/pre-order/{preOrder.documentId}
            </div>
          </div>
        ))
      )}

      {/* Modal for Add/Edit Pre-Order Item */}
      <div className="modal fade" id="preOrderItemModal" tabIndex="-1">
        <div className="modal-dialog">
          <div className="modal-content">
            <form onSubmit={handleSubmit}>
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingItem ? 'Modifier l\'article' : 'Nouvel article'}
                </h5>
                <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="product" className="form-label">Produit</label>
                  <select
                    className="form-select"
                    id="product"
                    name="product"
                    value={formData.product}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Sélectionner un produit</option>
                    {products.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.titre}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label htmlFor="start_date" className="form-label">Date de début</label>
                  <input
                    type="date"
                    className="form-control"
                    id="start_date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="end_date" className="form-label">Date de fin</label>
                  <input
                    type="date"
                    className="form-control"
                    id="end_date"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="price" className="form-label">Prix</label>
                  <input
                    type="number"
                    className="form-control"
                    id="price"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    required
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingItem ? 'Mettre à jour' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreOrders;